/**
 * ╔══════════════════════════════════════════════════════════════════════╗
 * ║         PEACHY BUTLERS — PRICING ENGINE  v1.0                       ║
 * ╟──────────────────────────────────────────────────────────────────────╢
 * ║  Business-rules layer. Zero DOM / framework dependencies.           ║
 * ║  Works in browser (global) and Node (CommonJS export).              ║
 * ║                                                                      ║
 * ║  EDITING PRICES: change values in PEACHY_CONFIG only.               ║
 * ║  Do not touch the engine section unless changing business logic.     ║
 * ╚══════════════════════════════════════════════════════════════════════╝
 */

// ─────────────────────────────────────────────────────────────────────────────
//  PEACHY_CONFIG  ·  All editable business rules live here
// ─────────────────────────────────────────────────────────────────────────────

const PEACHY_CONFIG = {

  // ── Base operating location ───────────────────────────────────────────────
  // Used as the origin for all travel distance calculations.
  // Lat/lng are cached so the engine works offline; postcodes.io resolves
  // the customer postcode dynamically.

  base: {
    postcode : 'BS1 1AA',
    lat      : 51.4521,
    lng      : -2.5932,
    label    : 'Bristol',
  },


  // ── Services ──────────────────────────────────────────────────────────────
  // Supported pricing.type values:
  //   'per-duration'  →  fixed price looked up from pricing.tiers[hours]
  //   'per-person'    →  pricing.pricePerPerson × guests
  //   'flat'          →  pricing.flatPrice regardless of duration/guests
  //
  // fixedDuration: if set, this duration is used and the UI hides the picker.
  // minGuests / maxGuests: validation limits (null = no limit).

  services: [
    {
      id          : 'single-butler',
      name        : 'Single Butler',
      tagline     : 'One charming Peachy Butler for your event.',
      category    : 'butler',
      butlerCount : 1,
      pricing: {
        type  : 'per-duration',
        tiers : {
          1 : 125,   // £125 for 1 hour
          2 : 160,   // £160 for 2 hours
          3 : 220,   // £220 for 3 hours
        },
      },
      durations   : [1, 2, 3],   // hours available for selection
      minGuests   : 1,
      maxGuests   : null,
    },

    {
      id          : 'double-butler',
      name        : 'Double Butler',
      tagline     : 'Two Peachy Butlers for double the fun.',
      category    : 'butler',
      butlerCount : 2,
      pricing: {
        type  : 'per-duration',
        tiers : {
          1 : 235,
          2 : 300,
          3 : 400,
        },
      },
      durations   : [1, 2, 3],
      minGuests   : 1,
      maxGuests   : null,
    },

    {
      id            : 'cocktail-class',
      name          : "Peachy's Cocktail Class",
      tagline       : 'Hands-on cocktail making with a shirtless Peachy Butler.',
      category      : 'activity',
      butlerCount   : 1,
      pricing: {
        type           : 'per-person',
        pricePerPerson : 40,      // £40 per guest
      },
      fixedDuration : 2,          // always 2 hrs — no picker shown
      durations     : null,
      minGuests     : 10,
      maxGuests     : null,
    },

    // ── Placeholder for future services ──────────────────────────────────────
    // Uncomment and fill in to add a service without touching engine logic.
    //
    // {
    //   id: 'double-trouble', name: 'Double Trouble',
    //   tagline: 'Our most spectacular two-butler experience.',
    //   category: 'butler', butlerCount: 2,
    //   pricing: { type: 'per-duration', tiers: { 2: 350, 3: 480 } },
    //   durations: [2, 3], minGuests: 1, maxGuests: null,
    // },
    //
    // {
    //   id: 'vip-package', name: 'VIP Package',
    //   category: 'butler', butlerCount: 2,
    //   pricing: { type: 'flat', flatPrice: 600 },
    //   fixedDuration: 4, durations: null, minGuests: 1, maxGuests: null,
    // },
  ],


  // ── Travel bands ─────────────────────────────────────────────────────────
  // Bands are evaluated in order; the first one where miles ≤ maxMiles wins.
  // surcharge: null  →  custom quote required, engine returns requiresCustomQuote: true.
  // All distances are straight-line (Haversine), not road miles.

  travelBands: [
    {
      id          : 'local',
      label       : 'Local',
      maxMiles    : 10,
      surcharge   : 0,          // £0 — no surcharge
      description : 'Within the Bristol & Bath area',
    },
    {
      id          : 'band-a',
      label       : 'Band A',
      maxMiles    : 25,
      surcharge   : 25,         // £ — adjust here
      description : '10–25 miles from Bristol',
    },
    {
      id          : 'band-b',
      label       : 'Band B',
      maxMiles    : 50,
      surcharge   : 50,         // £ — adjust here
      description : '25–50 miles from Bristol',
    },
    {
      id          : 'band-c',
      label       : 'Band C',
      maxMiles    : 75,
      surcharge   : 80,         // £ — adjust here
      description : '50–75 miles from Bristol',
    },
    {
      id          : 'custom',
      label       : 'Custom Quote Required',
      maxMiles    : Infinity,
      surcharge   : null,
      description : 'Over 75 miles — please contact us directly',
    },
  ],


  // ── Modifiers ─────────────────────────────────────────────────────────────
  // Applied after base price, before travel surcharge.
  // Set enabled: true and provide a condition function to activate.
  //
  // type: 'percentage'  →  value is a % added to the running price
  // type: 'flat'        →  value is a £ amount added directly
  //
  // condition receives the full context object from generateQuote / calculateQuote.

  modifiers: [
    // Unsociable hours surcharge (9pm–9am): flat £25
    {
      id      : 'unsociable-hours',
      label   : 'Late-night surcharge (9pm–9am)',
      enabled : true,
      type    : 'flat',
      value   : 25,
      condition: ({ startTime }) => {
        if (!startTime) return false;
        const [h, m] = startTime.split(':').map(Number);
        const mins = h * 60 + (m || 0);
        // 9pm = 1260 mins, 9am = 540 mins — wraps midnight
        return mins >= 21 * 60 || mins < 9 * 60;
      },
    },

    // Weekend surcharge example:
    // {
    //   id: 'weekend', label: 'Weekend Surcharge',
    //   enabled: false, type: 'percentage', value: 10,
    //   condition: ({ eventDate }) => {
    //     const d = new Date(eventDate).getDay();
    //     return d === 0 || d === 6;
    //   },
    // },

    // Peak season (June–August):
    // {
    //   id: 'peak-season', label: 'Peak Season',
    //   enabled: false, type: 'percentage', value: 15,
    //   condition: ({ eventDate }) => [6, 7, 8].includes(new Date(eventDate).getMonth() + 1),
    // },

    // Promotional discount (flat):
    // {
    //   id: 'promo-20', label: 'Promotion: £20 off',
    //   enabled: false, type: 'flat', value: -20,
    //   condition: ({ promoCode }) => promoCode === 'PEACHY20',
    // },
  ],
};


// ─────────────────────────────────────────────────────────────────────────────
//  PeachyPricingEngine  ·  Core calculation logic — edit pricing in config above
// ─────────────────────────────────────────────────────────────────────────────

const PeachyPricingEngine = (() => {

  // ── Internal helpers ───────────────────────────────────────────────────────

  /**
   * Straight-line distance between two lat/lng pairs (Haversine formula).
   * Returns miles.
   * @param {number} lat1
   * @param {number} lon1
   * @param {number} lat2
   * @param {number} lon2
   * @returns {number}
   */
  function _haversine(lat1, lon1, lat2, lon2) {
    const R   = 3958.8;                         // Earth radius in miles
    const rad = Math.PI / 180;
    const dLat = (lat2 - lat1) * rad;
    const dLon = (lon2 - lon1) * rad;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  /**
   * Resolve a UK postcode to lat/lng via postcodes.io (free, no key needed).
   * Throws a user-friendly Error if the postcode is invalid or the request fails.
   * @param {string} raw — raw postcode string, whitespace/case ignored
   * @returns {Promise<{ lat: number, lng: number }>}
   */
  async function _resolvePostcode(raw) {
    const pc = raw.replace(/\s+/g, '').toUpperCase();
    let res;
    try {
      res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(pc)}`);
    } catch {
      throw new Error('Unable to reach the postcode service. Please check your connection.');
    }
    if (!res.ok) {
      throw new Error(`Postcode "${raw}" could not be found. Please check and try again.`);
    }
    const { status, result } = await res.json();
    if (status !== 200 || !result) {
      throw new Error(`Postcode "${raw}" is not recognised. Please check and try again.`);
    }
    return { lat: result.latitude, lng: result.longitude };
  }

  /**
   * Calculate the base price for a service + inputs combo.
   * Throws on invalid/missing inputs so callers don't need to guard manually.
   */
  function _basePrice(service, duration, guests) {
    const { pricing: p } = service;

    if (p.type === 'per-duration') {
      const price = p.tiers[duration];
      if (price == null) {
        const valid = Object.keys(p.tiers).join(', ');
        throw new Error(`${duration} hour(s) is not available for ${service.name}. Valid options: ${valid}.`);
      }
      return price;
    }

    if (p.type === 'per-person') {
      if (guests < service.minGuests) {
        throw new Error(`${service.name} requires a minimum of ${service.minGuests} guests.`);
      }
      if (service.maxGuests && guests > service.maxGuests) {
        throw new Error(`${service.name} supports a maximum of ${service.maxGuests} guests.`);
      }
      return p.pricePerPerson * guests;
    }

    if (p.type === 'flat') {
      return p.flatPrice;
    }

    throw new Error(`Unknown pricing type: "${p.type}".`);
  }

  /**
   * Walk through PEACHY_CONFIG.modifiers and apply any that are enabled
   * and whose condition passes for the given context.
   * Returns the adjusted price and an array of applied modifier records.
   */
  function _applyModifiers(price, context) {
    const applied = [];

    for (const mod of PEACHY_CONFIG.modifiers) {
      if (!mod.enabled) continue;
      if (typeof mod.condition === 'function' && !mod.condition(context)) continue;

      let amount;
      if (mod.type === 'percentage') {
        amount = Math.round(price * (mod.value / 100));
      } else if (mod.type === 'flat') {
        amount = mod.value;
      } else {
        continue;
      }

      price += amount;
      applied.push({ id: mod.id, label: mod.label, amount });
    }

    return { price, applied };
  }


  // ── Public API ─────────────────────────────────────────────────────────────

  /**
   * Look up a service definition by ID.
   * Returns null if not found (does not throw).
   * @param {string} id
   * @returns {object|null}
   */
  function getService(id) {
    return PEACHY_CONFIG.services.find(s => s.id === id) ?? null;
  }

  /**
   * Return the travel band object for a given distance in miles.
   * @param {number} miles
   * @returns {object}
   */
  function getTravelBand(miles) {
    return (
      PEACHY_CONFIG.travelBands.find(b => miles <= b.maxMiles) ??
      PEACHY_CONFIG.travelBands.at(-1)
    );
  }

  /**
   * Synchronous quote calculation — use this when you already have a distance.
   *
   * @param {object} params
   * @param {string} params.serviceId
   * @param {number} params.duration    — hours; ignored for fixedDuration services
   * @param {number} params.guests
   * @param {number} params.miles       — straight-line distance from base
   * @param {object} [params.context]   — arbitrary extra data passed to modifier conditions
   *
   * @returns {QuoteResult}
   *   QuoteResult has requiresCustomQuote: true|false.
   *   When false: breakdown.estimatedTotal, breakdown.basePrice, breakdown.travelSurcharge.
   *   When true:  reason string explaining why (75+ miles).
   */
  function calculateQuote({ serviceId, duration, guests, miles, context = {} }) {
    const service = getService(serviceId);
    if (!service) throw new Error(`Unknown service: "${serviceId}".`);

    const effectiveDuration = service.fixedDuration ?? duration;
    const base              = _basePrice(service, effectiveDuration, guests);
    const { price: modified, applied: modifiers } = _applyModifiers(base, {
      serviceId, duration: effectiveDuration, guests, ...context,
    });
    const band = getTravelBand(miles);

    // ── Custom quote required (beyond Band C) ──────────────────────────────
    if (band.surcharge === null) {
      return {
        requiresCustomQuote : true,
        reason              : `Your event is approximately ${miles.toFixed(0)} miles from Bristol, which is beyond our standard travel zones. Please contact us directly for a personalised quote.`,
        service             : { id: service.id, name: service.name },
        guests,
        duration            : effectiveDuration,
        miles               : +miles.toFixed(1),
        travelBand          : { id: band.id, label: band.label, description: band.description },
      };
    }

    // ── Standard quote ─────────────────────────────────────────────────────
    return {
      requiresCustomQuote : false,
      service: {
        id          : service.id,
        name        : service.name,
        tagline     : service.tagline,
        butlerCount : service.butlerCount,
        category    : service.category,
      },
      guests,
      duration            : effectiveDuration,
      miles               : +miles.toFixed(1),
      travelBand: {
        id          : band.id,
        label       : band.label,
        description : band.description,
        surcharge   : band.surcharge,
      },
      breakdown: {
        basePrice       : base,
        modifiers,                          // [] when no modifiers are active
        travelSurcharge : band.surcharge,
        estimatedTotal  : modified + band.surcharge,
      },
    };
  }

  /**
   * Async quote generation with postcode lookup.
   * This is the primary public API for the customer-facing quote flow.
   *
   * @param {object} params
   * @param {string} params.serviceId
   * @param {number} params.duration
   * @param {number} params.guests
   * @param {string} params.postcode
   * @param {object} [params.context]   — forwarded to modifier conditions
   *
   * @returns {Promise<QuoteResult>}
   *
   * @example
   *   const quote = await PeachyPricingEngine.generateQuote({
   *     serviceId : 'single-butler',
   *     duration  : 2,
   *     guests    : 12,
   *     postcode  : 'BA1 2AB',
   *   });
   *   console.log(quote.breakdown.estimatedTotal); // → 185
   */
  async function generateQuote({ serviceId, duration, guests, postcode, startTime, context = {} }) {
    const service = getService(serviceId);
    if (!service) throw new Error(`Unknown service: "${serviceId}".`);

    // Normalise + validate inputs before the async call
    guests = parseInt(guests, 10);
    if (!Number.isFinite(guests) || guests < 1) {
      throw new Error('Please enter a valid number of guests.');
    }
    if (service.minGuests && guests < service.minGuests) {
      throw new Error(`${service.name} requires a minimum of ${service.minGuests} guests.`);
    }
    if (!postcode || postcode.trim().length < 5) {
      throw new Error('Please enter a valid UK postcode.');
    }

    const { lat, lng }          = await _resolvePostcode(postcode);
    const { lat: bLat, lng: bLng } = PEACHY_CONFIG.base;
    const miles                 = _haversine(bLat, bLng, lat, lng);

    return calculateQuote({ serviceId, duration, guests, miles, context: { ...context, startTime } });
  }

  // Public surface — only expose what callers need
  return {
    generateQuote,
    calculateQuote,
    getTravelBand,
    getService,
    config : PEACHY_CONFIG,
  };

})();

window.PeachyPricingEngine = PeachyPricingEngine;

